import { BackendProductUpsertOperationNameEnum } from "@credit-acceptance/step6-bff-client";
import { useEffect } from "react";
import { useController, type UseFormReturn } from "react-hook-form";

import { getErrorMessage } from "../../../../../lib/query/errors";
import {
  CheckboxField,
  CheckboxGroupField,
  CurrencyField,
  DateField,
  DropdownField,
  NumberField,
  TextField,
} from "../../Vehicle/fields/Fields";
import type { ObpFormValues } from "../Obp.mapping";
import { useVendorFormOptions } from "./useVendorFormOptions";

const BUNDLE_OPTIONS = [
  BackendProductUpsertOperationNameEnum.Maintenance,
  BackendProductUpsertOperationNameEnum.TireAndWheel,
  BackendProductUpsertOperationNameEnum.KeyReplacement,
  BackendProductUpsertOperationNameEnum.AppearanceRepairServices,
].map((name) => ({ label: name, value: name }));

interface StandardProductFieldsProperties {
  form: UseFormReturn<ObpFormValues>;
  index: number;
  productName: BackendProductUpsertOperationNameEnum;
  readOnly: boolean;
  /** Reports this product's vendor/form lookup failure (undefined once cleared) so the section can show it once. */
  onLookupError: (index: number, message?: string) => void;
}

export function StandardProductFields({
  form,
  index,
  productName,
  readOnly,
  onLookupError,
}: StandardProductFieldsProperties) {
  const { control, setValue } = form;
  const vendorName = useController({ control, name: `products.${index}.vendorName` });
  const certificateNumber = useController({ control, name: `products.${index}.certificateNumber` });
  const sellingPrice = useController({ control, name: `products.${index}.sellingPrice` });
  const termMonths = useController({ control, name: `products.${index}.termMonths` });
  const termMiles = useController({ control, name: `products.${index}.termMiles` });
  const unlimitedMileage = useController({ control, name: `products.${index}.unlimitedMileage` });
  const effectiveDate = useController({ control, name: `products.${index}.effectiveDate` });
  const formNumber = useController({ control, name: `products.${index}.formNumber` });
  const formId = useController({ control, name: `products.${index}.formId` });
  const selectedProducts = useController({ control, name: `products.${index}.selectedProducts` });
  const appearance = productName === BackendProductUpsertOperationNameEnum.AppearanceProtection;
  const bundle = productName === BackendProductUpsertOperationNameEnum.BundledProducts;
  const lookup = useVendorFormOptions(vendorName.field.value, formId.field.value, formNumber.field.value, readOnly);

  const lookupErrorMessage = lookup.error ? getErrorMessage(lookup.error) : undefined;
  useEffect(() => {
    onLookupError(index, lookupErrorMessage);
    return () => onLookupError(index);
  }, [index, lookupErrorMessage, onLookupError]);

  function handleVendorChange(value: string) {
    if (value !== vendorName.field.value) {
      vendorName.field.onChange(value);
      setValue(`products.${index}.formId`, null, { shouldDirty: true });
      setValue(`products.${index}.formNumber`, "", { shouldDirty: true });
      lookup.setFormSearch("");
    }
  }

  function handleFormNumberChange(value: string) {
    const option = lookup.formNumberOptions.find((entry) => entry.value === value);
    if (!option) {
      return;
    }
    formNumber.field.onChange(option.label);
    setValue(`products.${index}.formId`, option.formId ?? null, { shouldDirty: true });
  }

  return (
    <div className="flex flex-col gap-3">
      <DropdownField
        label="Vendor"
        required
        searchable
        value={vendorName.field.value}
        onChange={handleVendorChange}
        onBlur={vendorName.field.onBlur}
        onSearchChange={lookup.setVendorSearch}
        emptyMessage={lookup.vendorLoading ? "Loading vendors..." : "No vendors found"}
        options={lookup.vendorOptions}
        disabled={readOnly}
        error={vendorName.fieldState.error}
      />
      {!appearance && (
        <TextField
          label="Certificate Number"
          required
          value={certificateNumber.field.value}
          onChange={certificateNumber.field.onChange}
          onBlur={certificateNumber.field.onBlur}
          disabled={readOnly}
          error={certificateNumber.fieldState.error}
        />
      )}
      <CurrencyField
        label="Selling Price"
        required
        value={sellingPrice.field.value ?? undefined}
        onChange={(value) => sellingPrice.field.onChange(value ?? null)}
        onBlur={sellingPrice.field.onBlur}
        disabled={readOnly}
        error={sellingPrice.fieldState.error}
      />
      {!appearance && (
        <>
          <NumberField
            label="Term - Months"
            value={termMonths.field.value ?? undefined}
            onChange={(value) => termMonths.field.onChange(value ?? null)}
            onBlur={termMonths.field.onBlur}
            disabled={readOnly}
            error={termMonths.fieldState.error}
          />
          <NumberField
            label="Term - Miles"
            value={termMiles.field.value ?? undefined}
            onChange={(value) => termMiles.field.onChange(value ?? null)}
            onBlur={termMiles.field.onBlur}
            disabled={readOnly || (!bundle && Boolean(unlimitedMileage.field.value))}
            error={termMiles.fieldState.error}
          />
          {bundle ? (
            <CheckboxGroupField
              label="Products Included"
              required
              value={selectedProducts.field.value}
              onChange={selectedProducts.field.onChange}
              options={BUNDLE_OPTIONS}
              disabled={readOnly}
              error={selectedProducts.fieldState.error}
            />
          ) : (
            <div>
              <CheckboxField
                label="Unlimited Mileage"
                checked={unlimitedMileage.field.value}
                onCheckedChange={unlimitedMileage.field.onChange}
                disabled={readOnly}
              />
              {unlimitedMileage.fieldState.error?.message && (
                <p role="alert" className="text-destructive text-2xs">
                  {unlimitedMileage.fieldState.error.message}
                </p>
              )}
            </div>
          )}
        </>
      )}
      <DateField
        label="Effective Date"
        required
        value={effectiveDate.field.value}
        onChange={effectiveDate.field.onChange}
        onBlur={effectiveDate.field.onBlur}
        disabled={readOnly}
        error={effectiveDate.fieldState.error}
      />
      <DropdownField
        label="Form Number"
        required
        searchable
        value={formId.field.value?.toString() ?? formNumber.field.value}
        onChange={handleFormNumberChange}
        onBlur={formNumber.field.onBlur}
        onSearchChange={lookup.setFormSearch}
        emptyMessage={lookup.formsLoading ? "Loading forms..." : "No forms found"}
        options={lookup.formNumberOptions}
        disabled={readOnly || !vendorName.field.value}
        error={formNumber.fieldState.error}
      />
    </div>
  );
}
